/**
 * The takeoff module's server state: the 202-materialization protocol
 * client-side. generate answers 200 (the materialized BOM) or 202
 * (queued); while queued we poll gently -- the worker's stream events
 * also invalidate, whichever lands first.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_BASE, authFetch } from '../../api';

export interface TakeoffLineItem {
  kind: 'linear' | 'itemized';
  category: string | null;
  ifc_class: string;
  sku: string;
  quantity: number;
  unit: string;
  sticks: number | null;
  waste_inches: number | null;
  unit_price: number | null;
  total_cost: number | null;
  rate_provenance: string | null;
}

export interface TakeoffResult {
  project_id: string;
  element_count: number;
  line_items: TakeoffLineItem[];
  total_sticks: number;
  total_waste_inches: number;
  priced_cost: number;
  unpriced_lines: number;
}

type TakeoffState = { queued: true } | { queued: false; result: TakeoffResult };

const QUEUED_POLL_MS = 3000;

export function useTakeoff(projectId: string) {
  return useQuery<TakeoffState>({
    queryKey: ['takeoff', projectId],
    queryFn: async () => {
      const res = await authFetch(`${API_BASE}/v1/takeoff/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId }),
      });
      if (res.status === 202) return { queued: true };
      if (!res.ok) throw new Error(`takeoff: ${res.status}`);
      return { queued: false, result: (await res.json()) as TakeoffResult };
    },
    refetchInterval: (query) => (query.state.data?.queued ? QUEUED_POLL_MS : false),
    // The queued poll must run even when the tab is unfocused --
    // "this page updates itself" is a promise, not a focus-gated one.
    refetchIntervalInBackground: true,
  });
}

/** Re-materialize with current prices (refresh: true bypasses the
 * cached run); the query refetches on settle. */
export function useRefreshTakeoff(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await authFetch(`${API_BASE}/v1/takeoff/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId, refresh: true }),
      });
      if (!res.ok && res.status !== 202) throw new Error(`takeoff refresh: ${res.status}`);
      return res.status;
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['takeoff', projectId] }),
  });
}
